import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { PublishedCameraWatchService } from 'src/api/services';
import { PublishedCameraWatch } from 'src/api/models';
import { ApiConfiguration } from 'src/api/api-configuration';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { ToastrService } from 'ngx-toastr';
import { AzureAuthService } from 'src/service/azure-auth.service';

export interface CameraWatchViewItem {
  type: 'camera';
  id: string; // URI of the camera time series (unique identifier)
  name: string;
  uri?: string; // URI of the camera time series
}

export interface CameraWatchViewState {
  items: CameraWatchViewItem[];
}

@Component({
  selector: 'app-camera-watch',
  templateUrl: './camera-watch.component.html',
  styleUrls: ['./camera-watch.component.scss']
})
export class CameraWatchComponent implements OnInit, OnDestroy {
  @ViewChild('publishModal', { static: false }) publishModal: ModalDirective;
  
  private readonly STORAGE_KEY = 'cameraWatchView';
  private readonly MAX_CAMERAS = 9;
  private readonly REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

  public watchItems: CameraWatchViewItem[] = [];
  public showModal = false;
  public refreshTrigger$ = new Subject<void>();
  
  // Publication properties
  public publications: PublishedCameraWatch[] = [];
  public newPublication: Partial<PublishedCameraWatch> = {};
  public publicationsLoading = false;
  public isPublicView = false;
  public publishedIdentifier: string | null = null;
  
  private destroy$ = new Subject<void>();
  private refreshSubscription?: Subscription;
  private openPublishedCameraWatchService: PublishedCameraWatchService;

  constructor(
    public app: ApplicationConfigService,
    public azureAuthService: AzureAuthService,
    private router: Router,
    private route: ActivatedRoute,
    private readonly httpBackend: HttpBackend,
    private publishedCameraWatchService: PublishedCameraWatchService,
    private toastr: ToastrService
  ) {
    // Create a separate HTTP client that bypasses interceptors for public/unauthenticated calls
    const http = new HttpClient(httpBackend);
    this.openPublishedCameraWatchService = new PublishedCameraWatchService(new ApiConfiguration(), http);
  }

  ngOnInit(): void {
    // Check if this is a public view and handle identifier
    this.route.data.subscribe(data => {
      this.isPublicView = data['public'] === true;
      
      // Check for published identifier in route params
      const identifier = this.route.snapshot.params['identifier'];
      if (identifier && this.isPublicView) {
        this.publishedIdentifier = identifier;
        this.loadPublishedView(identifier);
      } else {
        if (this.app.customerId == null) {
          this.app.customerId = this.azureAuthService.userProfile.customerId;
        }
        this.loadFromLocalStorage();
      }
    });
    
    this.startRefreshTimer();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  private loadFromLocalStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY + '_' + this.app.customerId);
      if (stored) {
        const state: CameraWatchViewState = JSON.parse(stored);
        this.watchItems = state.items || [];
      }
    } catch (error) {
      console.error('Error loading camera watch view state from localStorage:', error);
      this.watchItems = [];
    }
  }

  private saveToLocalStorage(): void {
    try {
      const state: CameraWatchViewState = { items: this.watchItems };
      localStorage.setItem(this.STORAGE_KEY + '_' + this.app.customerId, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving camera watch view state to localStorage:', error);
      this.toastr.error('Failed to save watch view preferences');
    }
  }

  private startRefreshTimer(): void {
    this.refreshSubscription = interval(this.REFRESH_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.triggerRefresh();
      });
  }

  public triggerRefresh(): void {
    this.refreshTrigger$.next();
  }

  public openAddCameraModal(): void {
    if (this.watchItems.length >= this.MAX_CAMERAS) {
      this.toastr.warning(`Maximum of ${this.MAX_CAMERAS} cameras allowed`);
      return;
    }
    this.showModal = true;
  }

  public closeModal(): void {
    this.showModal = false;
  }

  public onItemsSelected(items: CameraWatchViewItem[]): void {
    // Add new items, respecting the max limit
    const availableSlots = this.MAX_CAMERAS - this.watchItems.length;
    const itemsToAdd = items.slice(0, availableSlots);
    
    // Filter out duplicates
    const newItems = itemsToAdd.filter(item => 
      !this.watchItems.some(existing => 
        existing.type === item.type && existing.id === item.id
      )
    );

    this.watchItems = [...this.watchItems, ...newItems];
    this.saveToLocalStorage();
    this.closeModal();

    if (itemsToAdd.length < items.length) {
      this.toastr.warning(`Only ${itemsToAdd.length} items added due to maximum limit`);
    }
  }

  public removeItem(item: CameraWatchViewItem): void {
    this.watchItems = this.watchItems.filter(i => 
      !(i.type === item.type && i.id === item.id)
    );
    this.saveToLocalStorage();
  }

  public getGridClass(): string {
    const count = this.watchItems.length;
    if (count === 0) return 'watch-grid-empty';
    if (count === 1) return 'watch-grid-1';
    if (count === 2) return 'watch-grid-2';
    if (count <= 4) return 'watch-grid-4';
    return 'watch-grid-9';
  }

  public get canAddMore(): boolean {
    return this.watchItems.length < this.MAX_CAMERAS;
  }

  public goHome(): void {
    this.router.navigate(['/home']);
  }

  // Publication methods
  public openPublishModal(): void {
    this.loadPublications();
    this.publishModal.show();
  }

  public closePublishModal(): void {
    this.publishModal.hide();
    this.newPublication = {};
  }

  public loadPublications(): void {
    if (!this.app.customerId) return;
    
    this.publicationsLoading = true;
    this.publishedCameraWatchService.PublishedCameraWatchGetAll(this.app.customerId)
      .subscribe(
        res => {
          // Filter to only show camera-watch publications
          this.publications = res.filter(p => p.type === 'camera-watch');
          this.publicationsLoading = false;
        },
        err => {
          console.error('Error loading publications:', err);
          this.toastr.error('Failed to load publications');
          this.publicationsLoading = false;
        }
      );
  }

  public createPublication(): void {
    if (!this.newPublication.linkValidToo) {
      this.toastr.error('Please select an expiration date');
      return;
    }

    const publication: PublishedCameraWatch = {
      id: 0,
      customerId: this.app.customerId!,
      linkValidToo: this.newPublication.linkValidToo,
      publishedDate: new Date().toISOString(),
      type: 'camera-watch',
      configJson: JSON.stringify({ items: this.watchItems }),
      publishedIdentifier: ''
    };

    this.publishedCameraWatchService.PublishedCameraWatchCreate({
      model: publication,
      customerId: this.app.customerId
    }).subscribe(
      res => {
        this.toastr.success('Publication created successfully');
        this.publications.push(res);
        this.newPublication = {};
      },
      err => {
        console.error('Error creating publication:', err);
        this.toastr.error('Failed to create publication');
      }
    );
  }

  public deletePublication(publication: PublishedCameraWatch): void {
    if (!confirm('Are you sure you want to delete this publication?')) {
      return;
    }

    this.publishedCameraWatchService.PublishedCameraWatchDelete({
      id: publication.id,
      customerId: this.app.customerId
    }).subscribe(
      () => {
        this.toastr.success('Publication deleted successfully');
        const index = this.publications.indexOf(publication);
        if (index > -1) {
          this.publications.splice(index, 1);
        }
      },
      err => {
        console.error('Error deleting publication:', err);
        this.toastr.error('Failed to delete publication');
      }
    );
  }

  public generatePublishedUrl(identifier: string): string {
    const baseUrl = document.baseURI;
    return `${baseUrl}#/publication/camera-watch/${identifier}`;
  }

  private loadPublishedView(identifier: string): void {
    // Use the open service that bypasses authentication interceptors
    this.openPublishedCameraWatchService.PublishedCameraWatchGetByIdentifier(identifier)
      .subscribe(
        res => {
          if (res && res.configJson) {
            try {
              const state: CameraWatchViewState = JSON.parse(res.configJson);
              this.watchItems = state.items || [];
              
              // Set customer context from published view
              if (res.customerId) {
                this.app.customerId = res.customerId;
              }
            } catch (error) {
              console.error('Error parsing published view config:', error);
              this.toastr.error('Failed to load published view');
            }
          } else {
            this.toastr.error('Published view not found or has expired');
          }
        },
        err => {
          console.error('Error loading published view:', err);
          this.toastr.error('Published view not found or has expired');
        }
      );
  }

  public get canPublish(): boolean {
    return (this.azureAuthService.isAdmin() || this.azureAuthService.isDashboardEditor()) && !this.isPublicView;
  }
}
